import { DateTime } from 'luxon';
import {
  getConnatixBlock,
  getDisplayAdBlock,
  getNativoAdBlock,
  IN_CONTENT_SLOT_BLOCK_NAME,
  isDisplayAdBlockName,
} from '@benzinga/ads-utils';
import { Block, MoneyBlockType, NodeLayout } from '@benzinga/content-manager';
import {
  countElementsInBodyBlocks,
  isTaxonomyExists,
  type ArticleBlock,
  type ArticleBlockPlaceholder,
  type ArticleData,
  type CampaignStrategy,
} from './index';
import {
  countPlacementUnits,
  findBreakIndices,
  selectBreakIndices,
  bodyAdCountForBreaks,
  MAX_BODY_DISPLAY_ADS,
  getBlockPlainText,
  isHeadingBlock,
  isSafeInterruptIndex,
  isShortNewsLength,
  MIN_PARAGRAPHS_BETWEEN_ADS,
  prepareBlocksForInterrupts,
  mergeUnbrokenListSplits,
  suggestedBodyAdCount,
} from './adPlacement';
import type { StoryCategory } from '@benzinga/advanced-news-manager';
import { DeviceType } from '@benzinga/device-utils';

type Blocks = ArticleBlock[] | ArticleBlockPlaceholder[] | MoneyBlockType[];

export interface ArticleAdsManagerOptions {
  nodeId?: number | string;
  contentType?: string;
  articleTitle?: string;
  articleBody?: string[];
  articleBlocks?: Blocks;
  articleUrl?: string;
  disableCampaignifyUnit?: boolean;
  hasAdLight?: boolean;
  isSponsored?: boolean;
  channels?: StoryCategory[];
  layout?: NodeLayout | null;
  createdDate?: string;
  deviceType?: DeviceType | null;
  tags?: StoryCategory[];
  ticker?: string;
  wordCount?: number | null;
  articleData?: ArticleData;
  googleAdPlaceholder?: Block;
  raptiveEnabled?: boolean;
}

export class ArticleAdsManager {
  public readonly AD_BLOCK_NAMES = [
    'ad/campaignify',
    'ad/optimizely-campaignify',
    'acf/raptive-ad-placement',
    'acf/freestar-ad-placement',
    'acf/nativo-ad-placement',
    'ad/primis',
    IN_CONTENT_SLOT_BLOCK_NAME,
    //'acf/connatix-live-block',
    'acf/taboola-placement',
  ];
  private readonly MIN_BLOCKS_BETWEEN_ADS = MIN_PARAGRAPHS_BETWEEN_ADS;
  private readonly NUMBER_OF_BODY_ADS = MAX_BODY_DISPLAY_ADS;
  private readonly MIN_PARAGRAPHS_BETWEEN_IMAGES = 2;
  private readonly SEARCH_RANGE = 20;
  private readonly MIN_PARAGRAPH_LENGTH = 130;
  private readonly MIN_REMAINING_BLOCKS = 2;
  private readonly SHORT_ARTICLE_WORD_COUNT = 200;
  private readonly FEW_PARAGRAPHS_ARTICLE_COUNT = 12;
  private readonly FIRST_AD_MIN_INDEX = 2;
  private readonly HEADER_PROXIMITY_RANGE = 1;
  private readonly RELATED_CONTENT_PROXIMITY_RANGE = 2;

  private readonly CONTENT_TYPE_FOR_HIDE = 'story';
  private readonly CHANNELS_FOR_HIDE = ['News', 'Markets'];
  private readonly COUNT_OF_ELEMENT_FOR_HIDE = 4;
  private readonly TYPE_OF_ELEMENT_FOR_HIDE = 'p';
  private readonly DAYS_OF_PUBLISHED_FOR_HIDE = 30;
  private readonly MINI_HEADER_TEXTS = ["Don't Miss:", 'Read Next:', 'Read More:', 'Read Now:', 'Also Read:'];
  private readonly RELATED_BLOCK_NAMES = ['acf/read-next', 'acf/taboola-placement'];
  private readonly RELATED_CLASS_SNIPPETS = ['bz-read-next-block', 'read-next-block', 'wp-block-benzinga-read-next'];

  private contentType?: string;
  private articleBlocks: Blocks;
  private articleUrl?: string;
  private channels?: StoryCategory[];
  private createdDate?: string;
  private deviceType?: DeviceType | null;
  private hasAdLight?: boolean;
  private isSponsored?: boolean;
  private layout?: NodeLayout | null;
  private strategy?: CampaignStrategy;
  private wordCount?: number | null;

  constructor(options: ArticleAdsManagerOptions) {
    this.contentType = options.contentType;
    this.articleBlocks = options.articleBlocks || [];
    this.articleUrl = options.articleUrl;
    this.channels = options.channels;
    this.createdDate = options.createdDate;
    this.deviceType = options.deviceType;
    this.hasAdLight = options.hasAdLight;
    this.isSponsored = options.isSponsored;
    this.layout = options.layout;
    this.wordCount = options.wordCount;
  }

  public excludeBlocks(blocks: Blocks): Blocks {
    if (!blocks) return [];
    return blocks.filter(block => !['acf/google-ad-placement'].includes(block.blockName || ''));
  }

  public checkIfShortArticle(count: number): boolean {
    return count ? count < this.SHORT_ARTICLE_WORD_COUNT : false;
  }

  public countParagraphsInBlocks(blocks: ArticleBlock[]): number {
    return countPlacementUnits(blocks);
  }

  public isShortNewsArticle(blocks: ArticleBlock[]): boolean {
    return isShortNewsLength(this.wordCount, this.countParagraphsInBlocks(blocks));
  }

  public checkIfFewParagraphsArticle(blocks: ArticleBlock[]): boolean {
    const paragraphCount = this.countParagraphsInBlocks(blocks);
    return paragraphCount < this.FEW_PARAGRAPHS_ARTICLE_COUNT;
  }

  public isNearElement(
    blocks: ArticleBlock[],
    index: number,
    range: number,
    checkIndex: boolean,
    checkBefore: boolean,
    checkAfter: boolean,
    checkFunc: (block: ArticleBlock) => boolean,
  ): boolean {
    if (checkIndex && index < blocks.length && checkFunc(blocks[index])) {
      return true;
    }

    if (checkBefore) {
      for (let i = Math.max(0, index - range); i < index; i++) {
        if (i < blocks.length && checkFunc(blocks[i])) {
          return true;
        }
      }
    }

    if (checkAfter) {
      for (let i = index + 1; i <= Math.min(blocks.length - 1, index + range); i++) {
        if (checkFunc(blocks[i])) {
          return true;
        }
      }
    }

    return false;
  }

  public isNearImageOrEmbed(
    blocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = true,
    type: 'all' | 'image' | 'embed' = 'all',
  ): boolean {
    const elementCheck = (block: ArticleBlock): boolean => {
      if (!block) return false;
      const isImage = !!block.tagAttributes?.className?.includes('wp-block-image');
      const isEmbed = !!block.tagAttributes?.className?.includes('wp-block-embed');
      return type === 'image' ? isImage : type === 'embed' ? isEmbed : isImage || isEmbed;
    };
    return this.isNearElement(blocks, index, range, true, checkBefore, checkAfter, elementCheck);
  }

  public isNearAd(
    blocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = true,
  ): boolean {
    const adCheck = (block: ArticleBlock): boolean => {
      return block ? this.AD_BLOCK_NAMES.includes(block.blockName) : false;
    };
    return this.isNearElement(blocks, index, range, true, checkBefore, checkAfter, adCheck);
  }

  public isHeaderBlock(block: ArticleBlock | undefined): boolean {
    if (!block) return false;
    if (this.isStructuralHeading(block)) return true;

    const plainText = getBlockPlainText(block);
    return this.MINI_HEADER_TEXTS.some(text => plainText.startsWith(text));
  }

  public isStructuralHeading(block: ArticleBlock | undefined): boolean {
    return isHeadingBlock(block);
  }

  public isRelatedContentBlock(block: ArticleBlock | undefined): boolean {
    if (!block) return false;
    if (block.blockName && this.RELATED_BLOCK_NAMES.includes(block.blockName)) return true;

    const className = block.tagAttributes?.className || '';
    if (typeof className === 'string' && this.RELATED_CLASS_SNIPPETS.some(snippet => className.includes(snippet))) {
      return true;
    }

    const html = (block.innerHTML || '').toLowerCase();
    return (
      html.includes('read next:') ||
      html.includes('read more:') ||
      html.includes('read now:') ||
      html.includes("don't miss:") ||
      html.includes('also read:') ||
      html.includes('related news:')
    );
  }

  public isNearHeader(
    blocks: ArticleBlock[],
    index: number,
    range: number = this.HEADER_PROXIMITY_RANGE,
    checkBefore = true,
    checkAfter = true,
  ): boolean {
    return this.isNearElement(blocks, index, range, true, checkBefore, checkAfter, block => this.isHeaderBlock(block));
  }

  public isNearRelatedContent(
    blocks: ArticleBlock[],
    index: number,
    range: number = this.RELATED_CONTENT_PROXIMITY_RANGE,
    checkBefore = true,
    checkAfter = true,
  ): boolean {
    return this.isNearElement(blocks, index, range, true, checkBefore, checkAfter, block =>
      this.isRelatedContentBlock(block),
    );
  }

  public isNearHorizontalRule(
    blocks: ArticleBlock[],
    index: number,
    range: number,
    checkBefore = true,
    checkAfter = false,
  ): boolean {
    return this.isNearElement(blocks, index, range, true, checkBefore, checkAfter, block => block?.tag === 'hr');
  }

  public isAppropriateForAdInsertion(blocks: ArticleBlock[], index: number, isFirstAd = false): boolean {
    if (index < 0 || index >= blocks.length) return false;

    const block = blocks[index];
    if (!block) return false;

    if (block.blockName === IN_CONTENT_SLOT_BLOCK_NAME) return false;
    if (!block.innerHTML) return false;

    if (this.isHeaderBlock(block) || this.isRelatedContentBlock(block)) {
      return false;
    }
    if (!isSafeInterruptIndex(blocks, index)) {
      return false;
    }

    const previousContent = this.findAdjacentContentBlock(blocks, index, 'before');
    const nextContent = this.findAdjacentContentBlock(blocks, index, 'after');
    if (this.isRelatedContentBlock(previousContent) || this.isRelatedContentBlock(nextContent)) {
      return false;
    }

    if (
      this.isNearImageOrEmbed(blocks, index, this.MIN_PARAGRAPHS_BETWEEN_IMAGES) ||
      this.isNearAd(blocks, index, this.MIN_BLOCKS_BETWEEN_ADS, true, !isFirstAd) ||
      (!isFirstAd && this.isNearRelatedContent(blocks, index, this.RELATED_CONTENT_PROXIMITY_RANGE, true, true)) ||
      this.isNearHorizontalRule(blocks, index, 1, true, true)
    ) {
      return false;
    }

    const plainText = getBlockPlainText(block);

    if (
      (block.tag && ['ul', 'ol', 'hr', 'figure'].includes(block.tag)) ||
      (block.tag === 'p' && plainText.length < this.MIN_PARAGRAPH_LENGTH)
    ) {
      return false;
    }

    return blocks.length - index >= this.MIN_REMAINING_BLOCKS;
  }

  public findNearestAppropriatePosition(
    blocks: ArticleBlock[],
    targetIndex: number,
    usedPositions: number[] = [],
    onlyCheckForward = false,
  ): number {
    const totalBlocks = blocks.length;

    if (
      targetIndex >= 0 &&
      targetIndex < totalBlocks &&
      this.isAppropriateForAdInsertion(blocks, targetIndex) &&
      !usedPositions.includes(targetIndex)
    ) {
      return targetIndex;
    }

    for (let i = 1; i < this.SEARCH_RANGE; i++) {
      const forward = targetIndex + i;
      if (
        forward < totalBlocks &&
        this.isAppropriateForAdInsertion(blocks, forward) &&
        !usedPositions.includes(forward)
      ) {
        return forward;
      }

      if (!onlyCheckForward) {
        const backward = targetIndex - i;
        if (backward >= 0 && this.isAppropriateForAdInsertion(blocks, backward) && !usedPositions.includes(backward)) {
          return backward;
        }
      }
    }

    return -1;
  }

  public findOptimalAdPositions(blocks: ArticleBlock[], numAds: number): number[] {
    if (!blocks || !blocks.length || numAds <= 0) return [];
    return selectBreakIndices(findBreakIndices(blocks), numAds);
  }

  public getAdBlock(position: number, adPositions: number[], _isMobile: boolean, displayOnly = false): ArticleBlock {
    if (displayOnly) return getDisplayAdBlock() as ArticleBlock;

    const slotIndex = adPositions.findIndex(slotPosition => slotPosition === position);
    const channelNames = this.channels?.map(channel => channel.name);
    if (slotIndex === 1) {
      return getConnatixBlock({ channels: channelNames, ...this.layout?.video_player }) as ArticleBlock;
    }
    if (slotIndex === 4) return getNativoAdBlock() as ArticleBlock;
    return getDisplayAdBlock() as ArticleBlock;
  }

  public insertAdsIntoArticle(blocks: ArticleBlock[], bodyAdCount?: number, displayOnly = false) {
    const isMobile = this.deviceType === 'mobile';
    const numberOfBodyAds = bodyAdCount ?? (isMobile ? this.NUMBER_OF_BODY_ADS + 1 : this.NUMBER_OF_BODY_ADS);
    const spacedBlocks = prepareBlocksForInterrupts(blocks);
    const adPositions = this.findOptimalAdPositions(spacedBlocks, numberOfBodyAds);
    let raptiveAdCount = 0;
    let totalAdCount = 0;

    const sortedPositions = [...adPositions].sort((a, b) => b - a);

    sortedPositions.forEach(position => {
      const adBlock = this.getAdBlock(position, adPositions, isMobile, displayOnly);
      spacedBlocks.splice(position, 0, adBlock);

      if (isDisplayAdBlockName(adBlock?.blockName)) {
        raptiveAdCount++;
      }
      totalAdCount++;
    });

    return {
      amountOfAds: totalAdCount,
      amountOfRaptiveAds: raptiveAdCount,
      blocks: mergeUnbrokenListSplits(spacedBlocks),
    };
  }

  public insertBelowArticleVideo(blocks: ArticleBlock[]): ArticleBlock[] {
    const channelNames = this.channels?.map(channel => channel.name);
    const videoBlock = getConnatixBlock({
      channels: channelNames,
      ...this.layout?.video_player,
    }) as ArticleBlock;

    return [...blocks, videoBlock];
  }

  public shouldHideCampaign(): boolean {
    if (!this.createdDate || !this.articleBlocks || !this.contentType || !this.channels) {
      return false;
    }

    const duration = DateTime.now().diff(DateTime.fromISO(this.createdDate), ['days']);

    return (
      this.contentType === this.CONTENT_TYPE_FOR_HIDE &&
      isTaxonomyExists(this.CHANNELS_FOR_HIDE, this.channels) &&
      countElementsInBodyBlocks(this.articleBlocks as ArticleBlock[], this.TYPE_OF_ELEMENT_FOR_HIDE) >
        this.COUNT_OF_ELEMENT_FOR_HIDE &&
      duration.days > this.DAYS_OF_PUBLISHED_FOR_HIDE
    );
  }

  public shouldRenderCampaign(content: string[] | Blocks): boolean {
    return (
      Array.isArray(content) &&
      content.length > 0 &&
      !this.isSponsored &&
      this.strategy !== 'none' &&
      !this.shouldHideCampaign()
    );
  }

  public shouldRenderAds(content: string[] | Blocks): boolean {
    return Array.isArray(content) && content.length > 0 && !this.isSponsored;
  }

  public findLastReadMorePosition(blocks: ArticleBlock[]): number {
    if (!blocks || !blocks.length) return -1;

    for (let i = blocks.length - 1; i >= 0; i--) {
      const content = blocks[i].innerHTML?.toLowerCase() || '';
      if (
        content.includes('<strong>read next:') ||
        content.includes('<strong>read more:') ||
        content.includes('<strong>read now:')
      ) {
        return i;
      }
    }
    return -1;
  }

  public formatArticleBlocks(blocks: Blocks): Blocks {
    if (!blocks || !blocks.length) return blocks;

    let newArticleBlocks: (MoneyBlockType | ArticleBlock)[] = [...blocks];

    if (this.shouldRenderAds(blocks) && !this.hasAdLight) {
      const isShortArticle = this.wordCount ? this.checkIfShortArticle(this.wordCount) : false;
      if (!isShortArticle) {
        const articleBlocks = newArticleBlocks as ArticleBlock[];
        const paragraphCount = this.countParagraphsInBlocks(articleBlocks);
        const suggestedCount = suggestedBodyAdCount(this.wordCount, paragraphCount);
        if (suggestedCount > 0) {
          const displayOnly = this.isShortNewsArticle(articleBlocks);
          const breakCount = findBreakIndices(prepareBlocksForInterrupts(articleBlocks)).length;
          const bodyAdCount = bodyAdCountForBreaks(suggestedCount, breakCount, displayOnly);
          const result = this.insertAdsIntoArticle(articleBlocks, bodyAdCount, displayOnly);
          newArticleBlocks = displayOnly ? this.insertBelowArticleVideo(result.blocks) : result.blocks;
        }
      }
    }

    return mergeUnbrokenListSplits(newArticleBlocks as ArticleBlock[]);
  }

  public formatArticle(): Blocks {
    return this.formatArticleBlocks(this.excludeBlocks(this.articleBlocks) || []);
  }

  private isSkippableProximityBlock(block: ArticleBlock | undefined): boolean {
    if (!block) return true;
    if (this.AD_BLOCK_NAMES.includes(block.blockName)) return true;
    if (this.isRelatedContentBlock(block) || this.isHeaderBlock(block)) return false;

    const plainText = (block.innerHTML || '').replace(/<[^>]*>/g, '').trim();
    if (!plainText) return true;

    const lowerText = plainText.toLowerCase();
    if (
      plainText.length < 50 &&
      (lowerText.startsWith('image via') ||
        lowerText.startsWith('photo via') ||
        lowerText.startsWith('image:') ||
        lowerText.startsWith('photo:'))
    ) {
      return true;
    }

    return false;
  }

  private findAdjacentContentBlock(
    blocks: ArticleBlock[],
    index: number,
    direction: 'before' | 'after',
    maxSteps = 3,
  ): ArticleBlock | undefined {
    const step = direction === 'before' ? -1 : 1;
    for (let offset = 1; offset <= maxSteps; offset++) {
      const candidateIndex = index + step * offset;
      if (candidateIndex < 0 || candidateIndex >= blocks.length) return undefined;
      const candidate = blocks[candidateIndex];
      if (this.isSkippableProximityBlock(candidate)) continue;
      return candidate;
    }
    return undefined;
  }
}

export default ArticleAdsManager;
